// src/QRCodeGenerator.js - Updated with PDF tracking and better limit messages
import React, { useState, useEffect } from 'react';
import useQRGenerator from './hooks/useQRGenerator';
import useFirestoreHistory from './hooks/useFirestoreHistory';
import useDynamicQR from './hooks/useDynamicQR';
import useBulkQRGenerator from './hooks/useBulkQRGenerator';
import { useAuth } from './AuthContext';
import { usePlanTracker } from './hooks/usePlanTracker';
import { useNavigate } from 'react-router-dom';
import { extractTextFromPDF, isValidPDF } from './utils/pdfExtractor';
import './QRCodeGenerator.css';
import QRCodeResults from './QRCodeResults';
import AuthModal from './AuthModal';
import SidebarPrompt from './SidebarPrompt';
import PromptInput from './PromptInput';

function QRCodeGenerator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const planTracker = usePlanTracker(user?.uid);
  const usageStats = planTracker?.getUsageStats();

  const dynamicQRHook = useDynamicQR(user?.uid);

  const {
    isProcessing,
    processingError,
    generatedQRCodes,
    aiResponse,
    generateQRFromPrompt,
    updateQRCode,
    deleteQRCode,
    clearAllQRCodes,
    setGeneratedQRCodes
  } = useQRGenerator(planTracker, user?.uid);

  const {
    isBulkProcessing,
    bulkProgress,
    bulkError,
    isChunkedMode,
    processPDFToQRCodes,
    continueChunkedProcessing,
    getChunkingStatus,
    resetBulkState
  } = useBulkQRGenerator(planTracker, user?.uid);

  const {
    chatHistory,
    isLoading: isHistoryLoading,
    saveChatSession,
    deleteChatSession,
    getRelativeTime,
  } = useFirestoreHistory();

  const [promptText, setPromptText] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isDynamicMode, setIsDynamicMode] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, sessionId: null });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessingPDF, setIsProcessingPDF] = useState(false);
  const [pdfError, setPdfError] = useState('');

  // Track chunking status
  const [chunkingStatus, setChunkingStatus] = useState(null);

  const showSignInPrompt = !user && generatedQRCodes.length > 0;
  const canUseDynamic = !!user;
  
  const dynamicQRLimit = planTracker?.planConfig?.limits?.dynamicQR || 0;
  const dynamicQRUsed = usageStats?.dynamicQRGenerated || 0;
  const dynamicQRRemaining = dynamicQRLimit === -1 ? 999 : (dynamicQRLimit - dynamicQRUsed);
  const hasDynamicQRLeft = dynamicQRLimit === -1 || dynamicQRRemaining > 0;

  // ✅ NEW: PDF upload limits
  const pdfUploadLimit = planTracker?.planConfig?.limits?.pdfUploadsPerMonth || 0;
  const pdfUploadsUsed = usageStats?.pdfUploadsUsed || 0;
  const pdfUploadsRemaining = pdfUploadLimit === -1 ? 999 : (pdfUploadLimit - pdfUploadsUsed);
  const hasPDFUploadLeft = usageStats?.canUploadPDF || false;

  const handleLogout = async () => {
    // TODO: Implement logout functionality
  };

  // Update chunking status whenever it changes
  useEffect(() => {
    if (isChunkedMode) {
      const status = getChunkingStatus();
      setChunkingStatus(status);
    } else {
      setChunkingStatus(null);
    }
  }, [isChunkedMode, isBulkProcessing, getChunkingStatus]);

  useEffect(() => {
    if (generatedQRCodes.length > 0 && promptText.trim() && !currentSessionId && user && aiResponse) {
      const saveSession = async () => {
        try {
          const sessionId = await saveChatSession(promptText, generatedQRCodes, aiResponse);
          if (sessionId) {
            setCurrentSessionId(sessionId);
          }
        } catch (error) {
          // Save failed silently
        }
      };
      saveSession();
    }
  }, [generatedQRCodes.length, promptText, aiResponse, currentSessionId, user, saveChatSession]);

  const handlePromptSubmit = async () => {
    if (!user) {
      setShowAuthModal(true);
      setAuthMode('login');
      return;
    }

    if (selectedFile) {
      await handlePDFProcess();
      return;
    }

    if (promptText.trim().length < 1) {
      alert('Please enter some text or upload a PDF');
      return;
    }

    if (isDynamicMode && !hasDynamicQRLeft) {
      alert(`You've used all ${dynamicQRLimit} dynamic QR code${dynamicQRLimit !== 1 ? 's' : ''} for your ${planTracker?.planConfig?.name || 'current'} plan. Please upgrade for unlimited dynamic QR codes.`);
      return;
    }

    if (promptText.trim().length > 1000) {
      alert('Prompt is too long. Please keep it under 1000 characters.');
      return;
    }

    setHasGenerated(true);
    setCurrentSessionId(null);

    try {
      // ✅ Regular QR generation (isFromPDF = false by default)
      await generateQRFromPrompt(promptText.trim(), isDynamicMode, false, false);
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setPdfError('');
    
    if (!file) return;

    if (!isValidPDF(file)) {
      setPdfError('Please upload a valid PDF file (max 10MB)');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handlePDFUploadClick = () => {
    if (!user) {
      setShowAuthModal(true);
      setAuthMode('login');
      return;
    }
  };

  const handlePDFProcess = async () => {
    if (!selectedFile) return;

    // ✅ CHECK PDF LIMIT FIRST
    if (!hasPDFUploadLeft) {
      const limitMessage = pdfUploadLimit === -1 
        ? 'Unable to upload PDF at this time.' 
        : `You've used all ${pdfUploadLimit} PDF upload${pdfUploadLimit !== 1 ? 's' : ''} this month.`;
      
      setPdfError(limitMessage);
      setSelectedFile(null);
      return;
    }

    setIsProcessingPDF(true);
    setPdfError('');
    setHasGenerated(true);
    setCurrentSessionId(null);

    try {
      const pdfText = await extractTextFromPDF(selectedFile);
      
      if (!pdfText || pdfText.length < 10) {
        throw new Error('Could not extract meaningful text from PDF');
      }

      // ✅ CONSUME PDF CREDIT BEFORE PROCESSING
      await planTracker.consumePDFUpload();

      // Clear existing QR codes before starting
      clearAllQRCodes();

      // ✅ Process PDF with isFromPDF = true (unlimited QR codes)
      // This wrapper function will pass isFromPDF = true to generateQRFromPrompt
      const generateQRWithPDFFlag = async (prompt, isDynamic, skipStateUpdate) => {
        return await generateQRFromPrompt(prompt, isDynamic, skipStateUpdate, true);
      };

      // Process the first chunk and set isChunkedMode if needed
      const newQRs = await processPDFToQRCodes(pdfText, generateQRWithPDFFlag, isDynamicMode);
      
      // Set the QR codes from first chunk
      if (newQRs && newQRs.length > 0) {
        setGeneratedQRCodes(newQRs);
      }

      setPromptText(`Bulk generation from: ${selectedFile.name}`);

    } catch (error) {
      setPdfError(error.message || 'Failed to process PDF');
      setHasGenerated(false);
    } finally {
      setIsProcessingPDF(false);
      setSelectedFile(null);
    }
  };

  // FIXED: Append QR codes from continuation
  const handleContinueChunk = async () => {
    if (!isChunkedMode || !chunkingStatus || chunkingStatus.isComplete) {
      return;
    }

    setIsProcessingPDF(true);
    setPdfError('');

    try {
      // ✅ Continue with isFromPDF = true flag
      const generateQRWithPDFFlag = async (prompt, isDynamic, skipStateUpdate) => {
        return await generateQRFromPrompt(prompt, isDynamic, skipStateUpdate, true);
      };

      const newQRs = await continueChunkedProcessing(generateQRWithPDFFlag, isDynamicMode);
      
      // FIXED: Append new QR codes to existing ones
      if (newQRs && newQRs.length > 0) {
        setGeneratedQRCodes(prev => [...prev, ...newQRs]);
      }

    } catch (error) {
      setPdfError(error.message || 'Failed to process next chunk');
    } finally {
      setIsProcessingPDF(false);
    }
  };

  const handleCancelPDF = () => {
    setSelectedFile(null);
    setPdfError('');
  };

  const handleNewPrompt = () => {
    setPromptText('');
    setHasGenerated(false);
    setCurrentSessionId(null);
    setIsDynamicMode(false);
    setSelectedFile(null);
    setPdfError('');
    setChunkingStatus(null);
    clearAllQRCodes();
    resetBulkState();
  };

  const loadChatSession = (session) => {
    setPromptText(session.promptText);
    setHasGenerated(true);
    setCurrentSessionId(session.id);
    
    const restoredQRCodes = session.qrCodes.map((qr) => ({
      ...qr,
      id: qr.id || `qr-${Date.now()}-${Math.random()}`
    }));
    
    setGeneratedQRCodes(restoredQRCodes);
  };

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation();
    setDeleteConfirmModal({ show: true, sessionId });
  };

  const confirmDelete = async () => {
    const sessionId = deleteConfirmModal.sessionId;
    setDeleteConfirmModal({ show: false, sessionId: null });
    
    const success = await deleteChatSession(sessionId);
    if (success && currentSessionId === sessionId) {
      handleNewPrompt();
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmModal({ show: false, sessionId: null });
  };

  const isAnyProcessing = isProcessing || isBulkProcessing || isProcessingPDF;

  return (
    <div className="app-wrapper">
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onSwitchMode={setAuthMode}
      />

      {deleteConfirmModal.show && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <h3>Delete chat</h3>
            </div>
            <div className="delete-modal-body">
              <p>Are you sure you want to delete this chat?</p>
            </div>
            <div className="delete-modal-footer">
              <button className="modal-btn modal-btn-cancel" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="modal-btn modal-btn-delete" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <SidebarPrompt
        user={user}
        chatHistory={chatHistory}
        isHistoryLoading={isHistoryLoading}
        currentSessionId={currentSessionId}
        usageStats={usageStats}
        planTracker={planTracker}
        dynamicQRLimit={dynamicQRLimit}
        dynamicQRUsed={dynamicQRUsed}
        onNewChat={handleNewPrompt}
        onLoadSession={loadChatSession}
        onDeleteSession={handleDeleteSession}
        onLogout={handleLogout}
        getRelativeTime={getRelativeTime}
      />

      <div className={`app-container ${hasGenerated ? 'has-results' : ''} ${!user ? 'no-sidebar' : ''}`}>
        {!hasGenerated && (
          <PromptInput
            user={user}
            usageStats={usageStats}
            planTracker={planTracker}
            promptText={promptText}
            setPromptText={setPromptText}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            isDynamicMode={isDynamicMode}
            setIsDynamicMode={setIsDynamicMode}
            isAnyProcessing={isAnyProcessing}
            isBulkProcessing={isBulkProcessing}
            bulkProgress={bulkProgress}
            processingError={processingError}
            pdfError={pdfError}
            bulkError={bulkError}
            canUseDynamic={canUseDynamic}
            hasDynamicQRLeft={hasDynamicQRLeft}
            dynamicQRLimit={dynamicQRLimit}
            dynamicQRRemaining={dynamicQRRemaining}
            dynamicQRUsed={dynamicQRUsed}
            onPromptSubmit={handlePromptSubmit}
            onPDFUploadClick={handlePDFUploadClick}
            onCancelPDF={handleCancelPDF}
            onFileSelect={handleFileSelect}
            showSignInPrompt={showSignInPrompt}
            // ✅ NEW: Pass PDF limit info
            hasPDFUploadLeft={hasPDFUploadLeft}
            pdfUploadLimit={pdfUploadLimit}
            pdfUploadsRemaining={pdfUploadsRemaining}
            pdfUploadsUsed={pdfUploadsUsed}
          />
        )}
     
        {hasGenerated && (
          <>
            <QRCodeResults
              promptText={promptText}
              aiResponse={aiResponse}
              isProcessing={isAnyProcessing}
              generatedQRCodes={generatedQRCodes}
              currentSessionId={currentSessionId}
              user={user}
              updateQRCode={updateQRCode}
              deleteQRCode={deleteQRCode}
              clearAllQRCodes={clearAllQRCodes}
              onNewGeneration={handleNewPrompt}
              dynamicQRHook={dynamicQRHook}
            />

            {/* Chunk Continuation Banner */}
            {isChunkedMode && chunkingStatus && !chunkingStatus.isComplete && (
              <div className="chunk-continuation-banner">
                <div className="chunk-info">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div className="chunk-text">
                    <strong>Large PDF Detected</strong>
                    <p>
                      Processed chunk {chunkingStatus.currentChunk} of {chunkingStatus.totalChunks}. 
                      {chunkingStatus.remainingChunks > 0 && (
                        <> {chunkingStatus.remainingChunks} chunk{chunkingStatus.remainingChunks !== 1 ? 's' : ''} remaining.</>
                      )}
                    </p>
                  </div>
                </div>
                <button 
                  className="btn-continue-chunk"
                  onClick={handleContinueChunk}
                  disabled={isAnyProcessing}
                >
                  {isAnyProcessing ? 'Processing...' : 'Continue Next Chunk'}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default QRCodeGenerator;